import React, { ReactNode } from 'react';
import {
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  ComposedChart,
} from 'recharts';
import { Formatter } from 'recharts/types/component/DefaultTooltipContent';
import { AxisInterval } from 'recharts/types/util/types';
import { Paper, useTheme } from '@mui/material';
import { OverlaySpinner } from '@ankr.com/ui';

import { Title } from '../Title';
import { SpendingHistoryBarChartData } from '../../types';
import { spendingValueFormatter } from './utils/spendingValueFormatter';
import { useContent } from '../RequestsChart/hooks/useContent';
import { useStackedBarChartWidgetStyles } from './StackedBarChartWidgetStyles';

interface IStackedBarChartWidgetProps {
  NoDataPlaceholder: () => JSX.Element;
  className?: string;
  csvDownloadButton?: ReactNode;
  data?: SpendingHistoryBarChartData[];
  isLightTheme: boolean;
  isLoading: boolean;
  title?: string;
  total?: ReactNode;
  tooltipValueFormatter?: Formatter<string, string>;
  yAxisTickDx?: number;
  yAxisTickFormatter: (value: number) => string;
  xAxisInterval?: AxisInterval;
  isAverageHidden: boolean;
}

const Y_AXIS_MAX_VALUE_COEFFICIENT = 1.1;

export const StackedBarChartWidget = ({
  NoDataPlaceholder,
  className,
  csvDownloadButton,
  data,
  isLoading,
  title,
  total,
  tooltipValueFormatter = spendingValueFormatter,
  yAxisTickDx = -5,
  yAxisTickFormatter,
  xAxisInterval = 0,
  isAverageHidden,
}: IStackedBarChartWidgetProps) => {
  const { classes, cx } = useStackedBarChartWidgetStyles();

  const { hasPreloader, hasChart, hasPlaceholder } = useContent({
    data,
    isLoading,
  });

  const hasDealChart =
    hasChart && data && data.some(item => Boolean(item.dealSpending));

  const theme = useTheme();

  return (
    <Paper className={cx(classes.root, className)}>
      {title && (
        <Title className={classes.title} csvButton={csvDownloadButton}>
          {title}
        </Title>
      )}
      {!!total && hasChart && total}
      {hasPreloader && <OverlaySpinner />}
      {hasPlaceholder && <NoDataPlaceholder />}
      {hasChart && (
        <>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data}>
              <CartesianGrid
                strokeDasharray="5 5"
                strokeWidth={0.3}
                horizontal
                vertical
                style={{ stroke: theme.palette.grey[100] }}
              />
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey="date"
                tick={{ fill: theme.palette.text.secondary, dy: 10 }}
                fontSize={12}
                tickSize={0}
                interval={xAxisInterval}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={yAxisTickFormatter}
                fontSize={12}
                tick={{ fill: theme.palette.text.secondary, dx: yAxisTickDx }}
                tickSize={0}
                width={45}
                domain={[
                  'auto',
                  (dataMax: number) => dataMax * Y_AXIS_MAX_VALUE_COEFFICIENT,
                ]}
              />

              {!isAverageHidden && (
                <Line
                  dot={false}
                  strokeWidth={2}
                  strokeLinecap="round"
                  dataKey="avg"
                  stroke="#A7AFC0"
                  strokeDasharray="10 10"
                  name="Average spend"
                />
              )}

              <Bar
                dataKey="paygSpending"
                fill="#EE538B"
                stackId="a"
                name="Pay As You Go"
              />
              {hasDealChart && (
                <Bar
                  dataKey="dealSpending"
                  fill="#356DF3"
                  stackId="a"
                  name="Deal"
                />
              )}
              <Tooltip
                contentStyle={{ fontSize: 12, color: 'black', borderRadius: 8 }}
                formatter={tooltipValueFormatter}
                cursor={false}
              />
              <Legend align="left" wrapperStyle={{ margin: '-16px 0' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </Paper>
  );
};
